type PrefetchHandler = (chunkId: string | number, promises: Promise<unknown>[]) => void;

type WebpackRequireLike = Readonly<{
  f?: Record<string, unknown>;
}>;

const guardedHandlers = new WeakSet<object>();

/**
 * The rspack runtime's prefetch handler (emitted because some `*.lazy.tsx` use `webpackPrefetch`)
 * runs `Promise.all(promises).then(prefetchChildren)` on every chunk load and drops the result, so a
 * failed chunk always leaves an unhandled rejection, even when the caller catches and retries it.
 * This hands the original handler a copy of the promises that stays pending on failure instead.
 */
export const guardPrefetchRejections = (webpackRequire: WebpackRequireLike | undefined): void => {
  const handlers = webpackRequire?.f;
  const original = handlers?.prefetch;
  if (!handlers || typeof original !== 'function' || guardedHandlers.has(original)) return;

  const prefetch = original as PrefetchHandler;
  const guarded: PrefetchHandler = (chunkId, promises) => {
    // On failure nothing was prefetched before either; the real promise still rejects for the caller.
    prefetch(chunkId, [Promise.all(promises).catch(() => new Promise<never>(() => {}))]);
  };
  guardedHandlers.add(guarded);
  handlers.prefetch = guarded;
};
